/**
 * "LAB3D Erhverv"-header (lys webshop-stil, navy chrome):
 *  - Slim top-strip (navy-900): USP'er + telefon/firmanavn
 *  - Hovedheader (navy): logo, stor hvid søgning m. orange knap, konto/
 *    favoritter/ordrer + kurv-knap med orange badge og mono-total
 *  - Nav-række (navy-800): kurateret kategorinav m. megadropdowns (HeaderNav)
 *  - Mobil: kompakt navy header m. søgefelt; USP-strip på forsiden
 * Al data kommer fra eksisterende hooks (auth, kurv, katalog, firma).
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  FileText,
  Heart,
  LogOut,
  Phone,
  Settings,
  ShoppingCart,
  Tag,
  Truck,
  User,
  UserRound,
} from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { useCart } from "@/features/cart/CartContext";
import { useActiveCompany, useSalespersonProfile } from "@/features/catalog/hooks/useActiveCompany";
import { CustomerSwitcher } from "@/features/sales/components/CustomerSwitcher";
import { formatKr } from "@/components/ui-lab";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { usePriceVisibility } from "@/features/catalog/PriceVisibilityContext";
import { LiveSearch } from "./LiveSearch";
import { HeaderNav } from "./HeaderNav";

/** LAB3D-logo til nav: rent ordmærke uden "ESTD. 2019" (læsbart i lille str.). */
function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <img
      src="/lab3d-logo-nav.png"
      alt="LAB3D"
      className={cn("w-auto", compact ? "h-7" : "h-9")}
    />
  );
}

/** Kurv-knap: hvidt kort, orange antal-badge, mono-total (desktop). */
function CartButton() {
  const navigate = useNavigate();
  const { totalItems, subtotalExVat } = useCart();
  const { hidePrices } = usePriceVisibility();
  return (
    <button
      type="button"
      onClick={() => navigate("/kurv")}
      aria-label={`Kurv, ${totalItems} varer, ${formatKr(subtotalExVat)}`}
      className="relative flex min-h-11 items-center gap-2.5 rounded-lg border border-white/15 bg-white/10 px-3.5 py-1.5 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
    >
      <span className="relative">
        <ShoppingCart className="h-5 w-5 text-white" aria-hidden />
        {totalItems > 0 && (
          <span
            aria-hidden
            className="absolute -right-2.5 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-cta px-1 text-[10px] font-bold leading-none text-white"
          >
            {totalItems > 99 ? "99+" : totalItems}
          </span>
        )}
      </span>
      <span className="font-mono text-sm font-bold tabular-nums text-white">
        {hidePrices ? "···" : formatKr(subtotalExVat)}
      </span>
    </button>
  );
}

/** Ikon+label-punkt i hovedheaderen (Min konto, Favoritter, Ordrer). */
function HeaderIconItem({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: typeof User;
}) {
  return (
    <Link
      to={to}
      className="flex min-w-14 flex-col items-center gap-1 rounded-md px-1.5 py-1 text-navy-100 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
    >
      <Icon className="h-5 w-5" aria-hidden />
      <span className="text-[11px] font-medium leading-none">{label}</span>
    </Link>
  );
}

/** Profilmenu bag "Min konto"-punktet. */
function AccountMenu() {
  const { profile, isSalesperson, isCompanyAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Min konto og profilmenu"
          className="flex min-w-14 flex-col items-center gap-1 rounded-md px-1.5 py-1 text-navy-100 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <UserRound className="h-5 w-5" aria-hidden />
          <span className="text-[11px] font-medium leading-none">Min konto</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">
          {[profile?.name, profile?.last_name].filter(Boolean).join(" ") || profile?.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/konto")}>
          <User className="mr-2 h-4 w-4" aria-hidden />
          Min konto
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/profil")}>Profil</DropdownMenuItem>
        {isCompanyAdmin && (
          <DropdownMenuItem onSelect={() => navigate("/firma")}>
            Firmaindstillinger
          </DropdownMenuItem>
        )}
        {isSalesperson && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/admin")}>
              <Settings className="mr-2 h-4 w-4" aria-hidden />
              Admin
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut className="mr-2 h-4 w-4" aria-hidden />
          Log ud
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** USP-række (hvid): 4 punkter på desktop, vandret scroll-strip på mobil. */
function UspRow() {
  const { data: company } = useActiveCompany();
  const { data: salesperson } = useSalespersonProfile(company?.salesperson_user_id);
  const discountPct = company?.default_discount_pct ?? null;
  const contactName = salesperson
    ? [salesperson.name, salesperson.last_name].filter(Boolean).join(" ")
    : null;

  const items = [
    { icon: Truck, title: "Afsendes samme dag", sub: "Bestil inden kl. 15" },
    { icon: FileText, title: "Faktura netto 30", sub: "Betal bagud på faktura" },
    {
      icon: Tag,
      title: discountPct && discountPct > 0 ? `Din faste rabat: ${Math.round(discountPct)} %` : "Faste B2B-priser",
      sub: "Beregnes automatisk",
    },
    {
      icon: Phone,
      title: "Fast kontaktperson",
      sub: contactName ?? "Vi kender dit setup",
    },
  ];

  return (
    <div className="border-b border-border bg-card">
      {/* Mobil: vandret scroll-strip */}
      <div className="flex gap-5 overflow-x-auto px-4 py-2.5 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map(({ icon: Icon, title }) => (
          <span key={title} className="flex shrink-0 items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span className="whitespace-nowrap text-xs font-bold text-foreground">{title}</span>
          </span>
        ))}
      </div>
      {/* Desktop: 4 kolonner */}
      <div className="mx-auto hidden max-w-[1400px] grid-cols-4 gap-4 px-4 py-3.5 md:grid">
        {items.map(({ icon: Icon, title, sub }) => (
          <div key={title} className="flex items-center justify-center gap-2.5">
            <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-bold text-foreground">{title}</p>
              <p className="truncate text-xs text-muted-foreground">{sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Header() {
  const { isSalesperson } = useAuth();
  const { data: company } = useActiveCompany();
  const location = useLocation();

  const isHome = location.pathname === "/dashboard";

  return (
    <header className="sticky top-0 z-40">
      {/* Top-strip (kun desktop) */}
      <div className="hidden bg-navy-900 md:block">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-1.5 text-xs text-navy-200">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5" aria-hidden />
              <span className="text-white/90">
                Fri fragt over 1.500 kr, afsendes samme dag inden kl. 15
              </span>
            </span>
            <span className="hidden items-center gap-1.5 lg:flex">
              <FileText className="h-3.5 w-3.5" aria-hidden />
              <span className="text-white/90">Faktura netto 30 dage</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+4521990307" className="flex items-center gap-1.5 hover:text-white">
              <Phone className="h-3.5 w-3.5" aria-hidden />
              +45 21 99 03 07
            </a>
            {company?.name && <span className="truncate text-white/90">{company.name}</span>}
          </div>
        </div>
      </div>

      {/* Hovedheader (navy) */}
      <div className="bg-navy">
        {/* Desktop */}
        <div className="mx-auto hidden h-[68px] max-w-[1400px] items-center gap-6 px-4 md:flex">
          <Link to="/dashboard" aria-label="Til forsiden" className="shrink-0">
            <Wordmark />
          </Link>
          <div className="min-w-0 flex-1">
            <LiveSearch />
          </div>
          {isSalesperson && (
            <div className="shrink-0">
              <CustomerSwitcher />
            </div>
          )}
          <div className="flex shrink-0 items-center gap-1.5">
            <AccountMenu />
            <HeaderIconItem to="/favoritter" label="Favoritter" icon={Heart} />
            <HeaderIconItem to="/ordrer" label="Ordrer" icon={ClipboardList} />
          </div>
          <CartButton />
        </div>

        {/* Mobil: kompakt app-header — kun søgefeltet i fuld bredde.
            Kurven bor i bundnavigationen (med antal-badge), og Kundevisning
            bor på Konto-siden, så admin og kunde ser samme forside. */}
        <div className="border-b border-border bg-background px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.625rem)] md:hidden">
          <LiveSearch variant="mobile" />
        </div>
      </div>

      {/* Nav-række (kun desktop): kurateret kategorinav m. megadropdowns */}
      <HeaderNav />

      {/* USP-række på forsiden */}
      {isHome && <UspRow />}
    </header>
  );
}
